import { type ComboboxData, InputWrapper, Select } from '@mantine/core'
import { useUncontrolled } from '@mantine/hooks'
import { formatCodeableConcept } from '@medplum/core'
import { referenceDisplay } from '@seen/core/display'
import type { IDT } from '@seen/core/models/care-teams/idt'
import { cn } from '@seen/ui'
import { compact } from 'lodash-es'
import type { JSX } from 'react'

type Props = {
  careTeam: IDT
  label?: string
  viewOnly?: boolean
  className?: string

  // input props
  onChange?: (value: string) => void
  value?: string
  defaultValue?: string
  error?: any
  onFocus?: any
  onBlur?: any
}

export function SDRIDTForm({
  careTeam,
  viewOnly,
  className,
  label,
  ...inputProps
}: Props): JSX.Element {
  const [value, handleChange] = useUncontrolled<string>(inputProps)

  const idtMemberSelectOptions = sdrIdtMemberOptions(careTeam)
  return (
    <InputWrapper
      label={label}
      className={cn('flex flex-col gap-2', className)}
      onFocus={inputProps.onFocus}
      onBlur={inputProps.onBlur}
      error={inputProps.error}
    >
      <Select
        searchable
        data={idtMemberSelectOptions}
        placeholder="Select..."
        {...inputProps}
        value={JSON.stringify(value)}
        onChange={(selectedValue: string | null) => {
          handleChange(selectedValue ? JSON.parse(selectedValue) : undefined)
        }}
        readOnly={viewOnly}
      />
    </InputWrapper>
  )
}

// Each option's value is the Reference saved as the disposition, so it keeps
// the stored display; only the label goes through referenceDisplay().
export function sdrIdtMemberOptions(
  careTeam: Pick<IDT, 'members' | 'name' | 'reference'> | undefined
): ComboboxData {
  return compact([
    careTeam
      ? {
          value: JSON.stringify(careTeam.reference),
          label: careTeam.name ?? 'Interdisciplinary Team (IDT)',
        }
      : undefined,
    careTeam && {
      group: 'IDT Member',
      items: careTeam.members.map(({ practitioner, role }) => {
        const code = formatCodeableConcept(role[0])
        return {
          value: JSON.stringify({
            ...practitioner,
            // lint-reference-display-ignore -- written to FHIR, not rendered
            display: `${practitioner.display} (${code})`,
          }),
          label: `${referenceDisplay(practitioner)} (${code})`,
        }
      }),
    },
  ])
}
